import { create } from 'zustand';
import { 
  collection, 
  doc, 
  onSnapshot, 
  query, 
  orderBy, 
  limit, 
  setDoc, 
  addDoc,
  getDoc,
  serverTimestamp, 
  where,
  updateDoc,
  arrayRemove,
  arrayUnion,
  getDocs,
  writeBatch,
  increment,
  deleteDoc
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase';
import { handleFirestoreError, OperationType } from '../utils/firestoreErrorHandler';

import { incrementSocialInteractions } from '../services/gameStatsService';

export interface Message {
  id: string;
  chatId: string;
  gameId?: string;
  participants?: string[];
  senderId: string;
  content: string | null;
  createdAt: any;
  type: 'text' | 'emoji' | 'voice' | 'image' | 'system';
  imageUrl?: string;
  thumbnailUrl?: string;
  storagePath?: string;
  deliveredTo: string[];
  readBy: string[];
  deletedForEveryone?: boolean;
  deletedFor?: string[];
}

export interface Chat {
  id: string;
  participants: string[];
  activeGameId?: string;
  lastMessage: string;
  lastMessageAt: any;
  lastMessageId?: string;
  lastSenderId: string;
  unreadBy: string[];
  unreadCounts?: Record<string, number>;
}

export interface ChatActivity {
  chatId: string;
  userId: string;
  isTyping: boolean;
  isRecording: boolean;
  updatedAt: any;
}

interface ChatState {
  chats: Record<string, Chat>;
  messages: Record<string, Message[]>;
  activity: Record<string, Record<string, ChatActivity>>;
  loading: boolean;
  subscribeToChats: (userId: string) => () => void;
  subscribeToMessages: (chatId: string, currentUserId?: string, deliveryConfirmationEnabled?: boolean) => () => void;
  subscribeToActivity: (chatId: string) => () => void;
  setActivity: (chatId: string, userId: string, isTyping: boolean, isRecording: boolean) => Promise<void>;
  sendMessage: (chatId: string, senderId: string, content: string, participants: string[], context?: { gameId?: string }) => Promise<void>;
  sendImageMessage: (chatId: string, senderId: string, file: File, participants: string[], content?: string, context?: { gameId?: string }) => Promise<void>;
  sendScreenshot: (chatId: string, senderId: string, imageData: string, participants: string[], content?: string, context?: { gameId?: string }) => Promise<void>;
  markAsRead: (chatId: string, userId: string, readReceiptsEnabled?: boolean, opponentReadReceiptsEnabled?: boolean) => Promise<void>;
  deleteMessage: (chatId: string, messageId: string, forEveryone: boolean, userId: string) => Promise<void>;
  clearChat: (chatId: string) => Promise<void>;
}

export const useChatStore = create<ChatState>((set) => ({
  chats: {},
  messages: {},
  activity: {},
  loading: false,

  subscribeToChats: (userId: string) => {
    const q = query(collection(db, 'chats'), where('participants', 'array-contains', userId));
    return onSnapshot(q, (snapshot) => {
      const newChats: Record<string, Chat> = {};
      snapshot.forEach((doc) => {
        newChats[doc.id] = { id: doc.id, ...doc.data() } as Chat;
      });
      set({ chats: newChats });
    }, (error) => {
      console.error('Error subscribing to chats:', error);
    });
  },

  subscribeToMessages: (chatId: string, currentUserId?: string, deliveryConfirmationEnabled: boolean = true) => {
    const q = query(
      collection(db, `chats/${chatId}/messages`),
      orderBy('createdAt', 'asc'),
      limit(100)
    );

    // Track delivered messages locally to avoid redundant writes in the same session
    const deliveredInSession = new Set<string>();

    return onSnapshot(q, async (snapshot) => {
      const newMessages: Message[] = [];
      const batch = writeBatch(db);
      let hasUpdates = false;

      snapshot.forEach((doc) => {
        const data = doc.data() as Message;
        newMessages.push({ id: doc.id, ...data });

        // Mark as delivered if received by recipient and not already delivered to them
        // AND if the recipient (current user) has delivery confirmation enabled
        if (
          currentUserId && 
          data.senderId !== currentUserId && 
          deliveryConfirmationEnabled && 
          !data.deliveredTo?.includes(currentUserId) &&
          !deliveredInSession.has(doc.id)
        ) {
          batch.update(doc.ref, { 
            deliveredTo: arrayUnion(currentUserId)
          });
          deliveredInSession.add(doc.id);
          hasUpdates = true;
        }
      });

      if (hasUpdates) {
        try {
          await batch.commit();
        } catch (error) {
          console.error('Error marking as delivered:', error);
        }
      }

      set((state) => ({
        messages: { ...state.messages, [chatId]: newMessages }
      }));
    }, (error) => {
      console.error('Error subscribing to messages:', error);
    });
  },

  subscribeToActivity: (chatId: string) => {
    const q = query(collection(db, 'chatActivity'), where('chatId', '==', chatId));
    return onSnapshot(q, (snapshot) => {
      const chatActivity: Record<string, ChatActivity> = {};
      snapshot.forEach((doc) => {
        const data = doc.data() as ChatActivity;
        chatActivity[data.userId] = data;
      });
      set((state) => ({
        activity: { ...state.activity, [chatId]: chatActivity }
      }));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, `chatActivity?chatId=${chatId}`);
    });
  },

  setActivity: async (chatId: string, userId: string, isTyping: boolean, isRecording: boolean) => {
    try {
      const activityId = `${chatId}_${userId}`;
      const activityRef = doc(db, 'chatActivity', activityId);
      await setDoc(activityRef, {
        chatId,
        userId,
        isTyping,
        isRecording,
        updatedAt: serverTimestamp()
      }, { merge: true });
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, `chatActivity/${chatId}_${userId}`);
    }
  },

  sendMessage: async (chatId: string, senderId: string, content: string, participants: string[], context?: { gameId?: string }) => {
    try {
      const messageData = {
        chatId,
        ...(context?.gameId ? { gameId: context.gameId } : {}),
        participants,
        senderId,
        content,
        createdAt: serverTimestamp(),
        type: 'text',
        deliveredTo: [senderId],
        readBy: [senderId]
      };

      const msgRef = await addDoc(collection(db, `chats/${chatId}/messages`), messageData);

      const unreadUpdates: Record<string, any> = {};
      participants.forEach(id => {
        if (id !== senderId) {
          unreadUpdates[`unreadCounts.${id}`] = increment(1);
        }
      });

      // Ensure chat exists and update last message
      await setDoc(doc(db, 'chats', chatId), {
        participants,
        lastMessage: content,
        lastMessageAt: serverTimestamp(),
        lastSenderId: senderId,
        lastMessageId: msgRef.id,
        ...(context?.gameId ? { activeGameId: context.gameId } : {}),
        unreadBy: arrayUnion(...participants.filter(id => id !== senderId)),
        ...unreadUpdates
      }, { merge: true });

      // Increment social interactions
      await incrementSocialInteractions(senderId);
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, `chats/${chatId}`);
    }
  },

  sendImageMessage: async (chatId: string, senderId: string, file: File, participants: string[], content?: string, context?: { gameId?: string }) => {
    try {
      // 1. Upload image to Storage
      const timestamp = Date.now();
      const safeFileName = file.name.replace(/[^\w.\-]+/g, '_');
      const storagePath = `chats/${chatId}/${senderId}_${timestamp}_${safeFileName}`;
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, file);
      const imageUrl = await getDownloadURL(storageRef);

      // 2. Create message in Firestore
      const finalContent = content || '📷 Imagen';
      const messageData = {
        chatId,
        ...(context?.gameId ? { gameId: context.gameId } : {}),
        participants,
        senderId,
        content: finalContent,
        type: 'image',
        imageUrl,
        thumbnailUrl: imageUrl, // For now use same, could generate thumbnail if needed
        storagePath,
        createdAt: serverTimestamp(),
        deliveredTo: [senderId],
        readBy: [senderId]
      };

      const msgRef = await addDoc(collection(db, `chats/${chatId}/messages`), messageData);

      const unreadUpdates: Record<string, any> = {};
      participants.forEach(id => {
        if (id !== senderId) {
          unreadUpdates[`unreadCounts.${id}`] = increment(1);
        }
      });

      await setDoc(doc(db, 'chats', chatId), {
        participants,
        lastMessage: finalContent,
        lastMessageAt: serverTimestamp(),
        lastSenderId: senderId,
        lastMessageId: msgRef.id,
        ...(context?.gameId ? { activeGameId: context.gameId } : {}),
        unreadBy: arrayUnion(...participants.filter(id => id !== senderId)),
        ...unreadUpdates
      }, { merge: true });
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, `chats/${chatId}/messages`);
      throw error;
    }
  },

  sendScreenshot: async (chatId: string, senderId: string, imageData: string, participants: string[], content?: string, context?: { gameId?: string }) => {
    try {
      // Convert base64 to blob for storage upload
      let response;
      try {
        response = await fetch(imageData);
      } catch (fetchError: any) {
        if (fetchError.name === 'AbortError') {
          console.log('[ChatStore] Screenshot fetch aborted');
          return;
        }
        throw fetchError;
      }
      const blob = await response.blob();
      const file = new File([blob], 'screenshot.png', { type: 'image/png' });
      
      await useChatStore.getState().sendImageMessage(chatId, senderId, file, participants, content || '📷 Captura de pantalla', context);
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, `chats/${chatId}/messages`);
    }
  },

  markAsRead: async (chatId: string, userId: string, readReceiptsEnabled: boolean = true, opponentReadReceiptsEnabled: boolean = true) => {
    try {
      const chatRef = doc(db, 'chats', chatId);
      const chatSnap = await getDoc(chatRef);
      
      if (chatSnap.exists()) {
        const chatData = chatSnap.data() as Chat;
        const opponentId = chatData.participants.find(id => id !== userId);
        
        // Update unreadBy in chat document
        await updateDoc(chatRef, {
          unreadBy: arrayRemove(userId),
          [`unreadCounts.${userId}`]: 0
        });

        // Update individual messages sent by opponent to be marked as read
        // ONLY if the user has read receipts enabled AND the opponent has them enabled (reciprocity)
        if (opponentId && readReceiptsEnabled && opponentReadReceiptsEnabled) {
          const messagesRef = collection(db, `chats/${chatId}/messages`);
          const q = query(
            messagesRef, 
            where('senderId', '==', opponentId),
            orderBy('createdAt', 'desc'),
            limit(50)
          );
          const messages = await getDocs(q);
          
          const unreadMessages = messages.docs.filter(doc => {
            const data = doc.data();
            return !data.readBy?.includes(userId);
          });
          
          if (unreadMessages.length > 0) {
            const batch = writeBatch(db);
            unreadMessages.forEach((msgDoc) => {
              batch.update(msgDoc.ref, { 
                readBy: arrayUnion(userId),
                deliveredTo: arrayUnion(userId) // If read, it must be delivered
              });
            });
            await batch.commit();
          }
        }
      }
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  },

  deleteMessage: async (chatId: string, messageId: string, forEveryone: boolean, userId: string) => {
    try {
      const msgRef = doc(db, `chats/${chatId}/messages`, messageId);
      const chatRef = doc(db, 'chats', chatId);

      if (forEveryone) {
        // Hard deleting message for everyone...
        
        // Check if it's an image and delete from storage
        const msgSnap = await getDoc(msgRef);
        if (msgSnap.exists()) {
          const msgData = msgSnap.data() as Message;
          if (msgData.type === 'image' && msgData.imageUrl) {
            try {
              if (msgData.storagePath) {
                const storageRef = ref(storage, msgData.storagePath);
                await deleteObject(storageRef);
              } else if (msgData.imageUrl.includes('firebasestorage.googleapis.com')) {
                const storageRef = ref(storage, msgData.imageUrl);
                await deleteObject(storageRef);
              }
            } catch (storageError) {
              console.error('Error deleting image from storage:', storageError);
            }
          }
        }

        // Check if this was the last message
        const chatSnap = await getDoc(chatRef);
        const isLastMessage = chatSnap.exists() && chatSnap.get('lastMessageId') === messageId;

        await deleteDoc(msgRef);
        
        // Check if there are any messages left in the chat
        const remainingMsgsQ = query(collection(db, `chats/${chatId}/messages`), limit(1));
        const remainingMsgsSnap = await getDocs(remainingMsgsQ);
        
        if (remainingMsgsSnap.empty) {
          await deleteDoc(chatRef);
        } else if (isLastMessage) {
          const q = query(
            collection(db, `chats/${chatId}/messages`),
            orderBy('createdAt', 'desc'),
            limit(1)
          );
          const snapshot = await getDocs(q);
          
          if (!snapshot.empty) {
            const lastMsg = snapshot.docs[0].data();
            await updateDoc(chatRef, {
              lastMessage: lastMsg.content || (lastMsg.type === 'image' ? '📷 Imagen' : 'Mensaje'),
              lastMessageAt: lastMsg.createdAt || serverTimestamp(),
              lastSenderId: lastMsg.senderId,
              lastMessageId: snapshot.docs[0].id
            });
          }
        }
      } else {
        await updateDoc(msgRef, {
          deletedFor: arrayUnion(userId)
        });
      }
    } catch (error) {
      console.error('Error in deleteMessage store action:', error);
      handleFirestoreError(error, OperationType.UPDATE, `chats/${chatId}/messages/${messageId}`);
      throw error;
    }
  },

  clearChat: async (chatId: string) => {
    try {
      const q = query(collection(db, `chats/${chatId}/messages`));
      const snapshot = await getDocs(q);
      const batch = writeBatch(db);
      snapshot.forEach((doc) => {
        batch.delete(doc.ref);
      });
      await batch.commit();
      
      // Delete the chat document itself to leave "no trace"
      await deleteDoc(doc(db, 'chats', chatId));
    } catch (error) {
      handleFirestoreError(error, OperationType.DELETE, `chats/${chatId}`);
    }
  }
}));

export const getChatId = (uid1: string, uid2: string) => {
  return [uid1, uid2].sort().join('_');
};
